import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/design-system/Button'
import { LanguageSwitcher } from '@/design-system/LanguageSwitcher'
import { Badge, BottomBarSpacer, Card, Container, Overline, Skeleton } from '@/design-system/primitives'
import { usePublicEvent } from '@/features/event/api'
import { useHandoverBalances } from '@/features/service/api'
import { HandoverPanel } from '@/features/service/components/HandoverPanel'
import { cn } from '@/lib/cn'
import { formatMoney } from '@/lib/money'
import { NoAccess, StaffGate, isForbidden, useApiErrorText } from '@/lib/staffGate'

import {
  useAddMovement,
  useCatalogue,
  useCloseSession,
  useMyMovements,
  useMySales,
  useMySession,
  useOpenSession,
  useRegisters,
  useSell,
  useVoidSale,
} from '../api'
import { DrawerPanel, ZReport } from '../components/TillPanels'
import type { CashRegister, CounterSale, Sale, SessionReport } from '../types'

/**
 * Le poste de vente.
 *
 * Conçu pour être tenu d'une main, debout, dans le bruit, par quelqu'un qui a
 * dix personnes devant lui. Tout en découle :
 *
 * * **Des cartes, pas une liste.** Une cible large se touche sans regarder.
 * * **Le panier toujours visible**, collé en bas sur mobile : le total est la
 *   seule chose qu'on doit pouvoir lire sans chercher.
 * * **Deux gestes pour encaisser** — le moyen de règlement, puis valider.
 * * **La monnaie en grand**, après la vente. C'est le chiffre qu'on lit à voix
 *   haute en rendant, et se tromper là coûte de l'argent à chaque fois.
 *
 * Aucun prix ne vient d'ici : le poste envoie des identifiants et des
 * quantités, le serveur calcule et fige. Un montant envoyé par le navigateur
 * serait un prix choisi par le vendeur.
 */
export function PosPage() {
  const { t } = useTranslation()
  return (
    <StaffGate title={t('pos.title')}>
      {(session, signOut) => <PosTerminal email={session.user.email} onSignOut={signOut} />}
    </StaffGate>
  )
}

function PosTerminal({ email, onSignOut }: { email: string; onSignOut: () => void }) {
  const { t, i18n } = useTranslation()
  const eventQuery = usePublicEvent()
  const eventId = eventQuery.data?.id

  const till = useMySession(eventId, true)
  const registers = useRegisters(eventId, !till.data)
  const catalogue = useCatalogue(eventId, Boolean(till.data))
  const open = useOpenSession(eventId)
  const close = useCloseSession(eventId)
  const sell = useSell(eventId)

  const [cart, setCart] = useState<Record<string, number>>({})
  const [method, setMethod] = useState('cash')
  const [tendered, setTendered] = useState('')
  // Facultatif : le prénom qu'on appellera quand le plat sort. Un verre part
  // avec le client ; un plat, non — la cuisine doit savoir à qui le tendre.
  const [customer, setCustomer] = useState('')
  const [receipt, setReceipt] = useState<Sale | null>(null)
  const [voiding, setVoiding] = useState(false)
  const [voidReason, setVoidReason] = useState('')
  const voidSale = useVoidSale(eventId)
  // Le tiroir se déplie à la demande : pendant le rush, l'écran est aux
  // cartes ; entre deux clients, on vérifie son théorique ou on note une sortie.
  const [drawerOpen, setDrawerOpen] = useState(false)
  // Le panier collé en bas grandit avec ses lignes : la page lui réserve sa
  // hauteur exacte, sinon les derniers produits passaient dessous.
  const barRef = useRef<HTMLDivElement>(null)
  const movements = useMyMovements(eventId, Boolean(till.data) && drawerOpen)
  const addMovement = useAddMovement(eventId)
  // Mes ventes de ce service : l'annulation ne dépend plus du ticket affiché.
  const [salesOpen, setSalesOpen] = useState(false)
  const sales = useMySales(eventId, Boolean(till.data) && salesOpen)
  // Qui a des espèces à remettre : visible sans ouvrir le tiroir. Un serveur
  // qui attend au comptoir avec ses billets ne doit pas dépendre d'un clic
  // que personne n'a pensé à faire.
  const balances = useHandoverBalances(eventId, Boolean(till.data))
  const holders = (balances.data ?? []).filter((row) => row.held_minor > 0)
  const errorText = useApiErrorText()
  // Le Z de la session qu'on vient de fermer. Sans lui, la fermeture
  // ramenait directement à l'écran d'ouverture : le caissier ne voyait jamais
  // son propre rapport, ni l'écart qu'il venait de motiver.
  const [lastZ, setLastZ] = useState<SessionReport | null>(null)

  useEffect(() => {
    document.title = t('pos.title')
  }, [t])

  const items = catalogue.data?.items ?? []
  const currency = catalogue.data?.currency ?? 'EUR'
  const money = (minor: number) => formatMoney({ amount_minor: minor, currency }, i18n.language)

  const total = useMemo(
    () =>
      Object.entries(cart).reduce((sum, [key, quantity]) => {
        const product = items.find((row) => `${row.kind}:${row.ref_id}` === key)
        return sum + (product ? product.price_minor * quantity : 0)
      }, 0),
    [cart, items],
  )
  const count = Object.values(cart).reduce((sum, quantity) => sum + quantity, 0)

  if (eventQuery.isPending || till.isPending) {
    return (
      <Container className="py-16">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="mt-6 h-64 w-full" />
      </Container>
    )
  }

  // Un refus n'est pas « aucune caisse configurée » : ce compte n'a simplement
  // pas de caisse à tenir. Le message d'avant envoyait chercher une caisse
  // dans le back-office alors qu'elle existait.
  if (isForbidden(till.error) || isForbidden(registers.error)) {
    return <NoAccess title={t('pos.title')} onSignOut={onSignOut} />
  }

  // ── Pas de caisse ouverte : la prise de poste passe avant tout ──
  if (!till.data) {
    return (
      <Container className="max-w-md py-16">
        <Header email={email} onSignOut={onSignOut} />
        {lastZ ? (
          <Card className="mt-8 border-2 border-champagne-400/40">
            <div className="flex flex-wrap items-baseline justify-between gap-3">
              <p className="overline text-content-muted">{t('pos.z.title')}</p>
              <span className="text-sm text-content-secondary">
                {lastZ.register_label} · {lastZ.user_label}
              </span>
            </div>
            <ZReport
              report={lastZ}
              money={(minor) =>
                formatMoney({ amount_minor: minor, currency: lastZ.currency }, i18n.language)
              }
              className="mt-4"
            />
            <Button variant="ghost" size="sm" className="mt-4" onClick={() => setLastZ(null)}>
              {t('pos.z.dismiss')}
            </Button>
          </Card>
        ) : null}
        <Card className="mt-8">
          <h1 className="font-display text-heading-lg text-content-primary">
            {t('pos.openTitle')}
          </h1>
          <p className="mt-2 text-sm text-content-secondary">{t('pos.openBody')}</p>

          <OpenForm
            registers={registers.data ?? []}
            loaded={registers.isSuccess}
            busy={open.isPending}
            error={open.isError ? errorText(open.error) : null}
            onOpen={(payload) => open.mutate(payload)}
          />
        </Card>
      </Container>
    )
  }

  const report = till.data

  return (
    <Container className="py-8">
      <Header email={email} onSignOut={onSignOut} />

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <Badge tone="success">{report.register_label}</Badge>
        <span className="text-sm text-content-secondary">
          {t('pos.expected')} : <strong className="tabular-nums">{money(report.expected_minor)}</strong>
        </span>
        <Button variant="outline" size="sm" onClick={() => setDrawerOpen(!drawerOpen)}>
          {drawerOpen ? t('pos.drawer.hide') : t('pos.drawer.show')}
        </Button>
        <Button variant="outline" size="sm" onClick={() => setSalesOpen(!salesOpen)}>
          {salesOpen ? t('pos.sales.hide') : t('pos.sales.show')}
        </Button>
        {holders.length > 0 && !drawerOpen ? (
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            className="rounded-full border border-champagne-400/60 bg-champagne-400/10 px-3 py-1.5 text-sm text-champagne-300"
          >
            {t('pos.handover.badge', {
              count: holders.length,
              amount: money(holders.reduce((sum, row) => sum + row.held_minor, 0)),
            })}
          </button>
        ) : null}
        <CloseTill
          expected={report.expected_minor}
          money={money}
          busy={close.isPending}
          error={close.isError ? close.error.message : null}
          onClose={(payload) =>
            close.mutate(payload, {
              onSuccess: (closed) => {
                setLastZ(closed)
                setDrawerOpen(false)
                setReceipt(null)
              },
            })
          }
        />
      </div>

      {drawerOpen ? (
        <>
          <Card className="mt-6">
            <p className="overline text-content-muted">{t('pos.z.live')}</p>
            <ZReport report={report} money={money} className="mt-3" />
          </Card>
          <DrawerPanel
            movements={movements.data ?? []}
            money={money}
            busy={addMovement.isPending}
            error={addMovement.isError ? addMovement.error.message : null}
            onAdd={(payload, done) => addMovement.mutate(payload, { onSuccess: done })}
          />
          {/* Les serveurs viennent vider leur poche ici : ce qu'ils ont
              encaissé à table entre dans ce tiroir, à leur nom. */}
          <HandoverPanel eventId={eventId} money={money} />
        </>
      ) : null}

      {salesOpen ? (
        <SalesPanel
          sales={sales.data ?? []}
          loading={sales.isPending}
          money={money}
          busy={voidSale.isPending}
          error={voidSale.isError ? errorText(voidSale.error) : null}
          onVoid={(orderId, reason, done) =>
            voidSale.mutate(
              { orderId, reason },
              {
                onSuccess: () => {
                  done()
                  if (receipt?.order_id === orderId) setReceipt(null)
                },
              },
            )
          }
        />
      ) : null}

      {/* Le ticket de la vente précédente : la monnaie à rendre, en grand. */}
      {receipt ? (
        <Card className="mt-6 border-2 border-state-success">
          <div className="flex flex-wrap items-baseline justify-between gap-4">
            <div className="min-w-0">
              <p className="overline text-content-muted">{t('pos.sold')}</p>
              <p className="font-display text-heading-lg text-content-primary">
                {money(receipt.total_minor)}
              </p>
              {/* Le numéro, et le prénom s'il a été donné : c'est ce que le
                  poste appellera quand la commande sera prête. */}
              <p className="mt-1 break-words text-sm text-content-secondary">
                <span className="font-mono">{receipt.order_number}</span>
                {receipt.customer_label
                  ? ` · ${t('pos.callName', { name: receipt.customer_label })}`
                  : ''}
              </p>
            </div>
            {receipt.change_due_minor !== null ? (
              <div className="text-right">
                <p className="overline text-content-muted">{t('pos.change')}</p>
                <p className="font-display text-display-sm tabular-nums text-state-success">
                  {money(receipt.change_due_minor)}
                </p>
              </div>
            ) : null}
          </div>
          {/* L'annulation vit **sur le ticket**, tant qu'il est à l'écran :
              c'est là qu'on s'aperçoit de l'erreur, avec le client encore
              devant soi. La chercher ailleurs, au milieu du service, revient à
              ne pas l'avoir. */}
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => setReceipt(null)}>
              {t('pos.next')}
            </Button>
            {voiding ? (
              <>
                <input
                  value={voidReason}
                  onChange={(event) => setVoidReason(event.target.value)}
                  placeholder={t('pos.voidReason')}
                  className="w-48 rounded-md border border-line bg-bg-base px-3 py-1.5 text-sm text-content-primary"
                />
                <Button
                  variant="ember"
                  size="sm"
                  disabled={voidSale.isPending || voidReason.trim().length < 2}
                  onClick={() =>
                    voidSale.mutate(
                      { orderId: receipt.order_id, reason: voidReason.trim() },
                      {
                        onSuccess: () => {
                          setReceipt(null)
                          setVoiding(false)
                          setVoidReason('')
                        },
                      },
                    )
                  }
                >
                  {t('pos.voidConfirm')}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setVoiding(false)}>
                  {t('pos.voidCancel')}
                </Button>
              </>
            ) : (
              <Button variant="ghost" size="sm" onClick={() => setVoiding(true)}>
                {t('pos.void')}
              </Button>
            )}
          </div>
          {voidSale.error && !salesOpen ? (
            <p className="mt-2 text-sm text-state-danger">{errorText(voidSale.error)}</p>
          ) : null}
        </Card>
      ) : null}

      {catalogue.isPending ? <Skeleton className="mt-6 h-64 w-full" /> : null}

      <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {items.map((product) => {
          const key = `${product.kind}:${product.ref_id}`
          const inCart = cart[key] ?? 0
          const soldOut = product.remaining === 0

          return (
            <button
              key={key}
              type="button"
              disabled={soldOut}
              onClick={() => setCart((current) => ({ ...current, [key]: (current[key] ?? 0) + 1 }))}
              className={cn(
                'relative flex min-h-24 flex-col justify-between rounded-lg border p-3 text-left transition',
                soldOut
                  ? 'border-line-subtle opacity-40'
                  : 'border-line bg-bg-elevated active:scale-[.98]',
                inCart > 0 && 'border-champagne-400',
              )}
            >
              <span className="text-sm leading-snug text-content-primary">{product.label}</span>
              <span className="mt-2 font-display text-heading-sm tabular-nums text-champagne-300">
                {money(product.price_minor)}
              </span>

              {inCart > 0 ? (
                <span className="absolute right-2 top-2 flex h-6 min-w-6 items-center justify-center rounded-full bg-ember-500 px-1.5 font-mono text-caption text-white">
                  {inCart}
                </span>
              ) : null}
              {soldOut ? (
                <span className="absolute right-2 top-2 text-caption text-content-muted">
                  {t('pos.soldOut')}
                </span>
              ) : null}
            </button>
          )
        })}
      </div>

      {items.length === 0 && !catalogue.isPending ? (
        <p className="mt-6 text-sm text-content-muted">{t('pos.emptyCatalogue')}</p>
      ) : null}

      {/* Le panier, collé en bas : le total ne doit jamais sortir de l'écran. */}
      <BottomBarSpacer barRef={barRef} className="lg:hidden" />
      <div
        ref={barRef}
        className="fixed inset-x-0 bottom-0 z-20 border-t border-line bg-bg-elevated/95 pb-[env(safe-area-inset-bottom)] backdrop-blur lg:static lg:mt-8 lg:rounded-lg lg:border lg:pb-0"
      >
        <div className="mx-auto max-w-6xl px-4 py-4">
          <div className="flex items-baseline justify-between gap-4">
            <span className="text-sm text-content-secondary">
              {t('pos.cart', { count })}
            </span>
            <span className="font-display text-display-sm tabular-nums text-content-primary">
              {money(total)}
            </span>
          </div>

          {count > 0 ? (
            <>
              <div className="mt-3 max-h-28 overflow-y-auto">
                {Object.entries(cart)
                  .filter(([, quantity]) => quantity > 0)
                  .map(([key, quantity]) => {
                    const product = items.find((row) => `${row.kind}:${row.ref_id}` === key)
                    if (!product) return null
                    return (
                      <div key={key} className="flex items-center justify-between gap-3 py-1">
                        <span className="min-w-0 truncate text-sm text-content-secondary">
                          {quantity} × {product.label}
                        </span>
                        <div className="flex shrink-0 gap-1">
                          <button
                            type="button"
                            aria-label={`− ${product.label}`}
                            onClick={() =>
                              setCart((current) => {
                                const next = { ...current }
                                if ((next[key] ?? 0) <= 1) delete next[key]
                                else next[key] = (next[key] ?? 0) - 1
                                return next
                              })
                            }
                            className="h-10 w-10 rounded-full border border-line text-content-primary"
                          >
                            −
                          </button>
                          <button
                            type="button"
                            aria-label={`+ ${product.label}`}
                            onClick={() =>
                              setCart((current) => ({ ...current, [key]: (current[key] ?? 0) + 1 }))
                            }
                            className="h-10 w-10 rounded-full border border-line text-content-primary"
                          >
                            +
                          </button>
                        </div>
                      </div>
                    )
                  })}
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-2">
                {['cash', 'card', 'paypal'].map((option) => (
                  <Button
                    key={option}
                    size="sm"
                    variant={method === option ? 'ember' : 'outline'}
                    onClick={() => setMethod(option)}
                  >
                    {t(`pos.methods.${option}`)}
                  </Button>
                ))}

                {method === 'cash' ? (
                  <input
                    inputMode="decimal"
                    value={tendered}
                    onChange={(event) => setTendered(event.target.value)}
                    placeholder={t('pos.tendered')}
                    className="h-9 w-28 rounded-md border border-line bg-bg-base px-3 text-sm text-content-primary"
                  />
                ) : null}
              </div>

              <input
                value={customer}
                maxLength={60}
                autoComplete="off"
                autoCapitalize="words"
                onChange={(event) => setCustomer(event.target.value)}
                placeholder={t('pos.customerName')}
                aria-label={t('pos.customerName')}
                className="mt-3 h-10 w-full rounded-md border border-line bg-bg-base px-3 text-sm text-content-primary"
              />

              <div className="mt-3 flex gap-2">
                <Button
                  size="lg"
                  className="flex-1"
                  disabled={sell.isPending}
                  onClick={() => {
                    const value = Number.parseFloat(tendered.replace(',', '.'))
                    sell.mutate(
                      {
                        lines: Object.entries(cart)
                          .filter(([, quantity]) => quantity > 0)
                          .map(([key, quantity]) => {
                            const [kind, ref_id] = key.split(':')
                            return { kind: kind ?? '', ref_id: ref_id ?? '', quantity }
                          }),
                        payment_method: method,
                        tendered_minor:
                          method === 'cash' && Number.isFinite(value)
                            ? Math.round(value * 100)
                            : null,
                        note: null,
                        customer_label: customer.trim() || null,
                      },
                      {
                        onSuccess: (sale) => {
                          setReceipt(sale)
                          setCart({})
                          setTendered('')
                          setCustomer('')
                        },
                      },
                    )
                  }}
                >
                  {sell.isPending ? t('pos.selling') : t('pos.charge')}
                </Button>
                <Button variant="ghost" size="lg" onClick={() => setCart({})}>
                  {t('pos.clear')}
                </Button>
              </div>

              {sell.isError ? (
                <p className="mt-2 text-sm text-state-danger">{sell.error.message}</p>
              ) : null}
            </>
          ) : null}
        </div>
      </div>
    </Container>
  )
}

function Header({ email, onSignOut }: { email: string; onSignOut: () => void }) {
  const { t } = useTranslation()

  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      {/* La langue à côté du nom de l'écran : le coin « compte » garde
          sa place, et rien ne pousse « Se déconnecter » hors de l'écran. */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <Overline>{t('pos.title')}</Overline>
        <LanguageSwitcher />
      </div>
      <div className="flex items-center gap-3 text-sm text-content-muted">
        <span>{email}</span>
        <Button variant="ghost" size="sm" onClick={onSignOut}>
          {t('checkin.logout')}
        </Button>
      </div>
    </div>
  )
}

function OpenForm({
  registers,
  loaded,
  busy,
  error,
  onOpen,
}: {
  registers: CashRegister[]
  /** La liste est arrivée : « aucune caisse » ne se dit pas pendant qu'on charge. */
  loaded: boolean
  busy: boolean
  error: string | null
  onOpen: (payload: { register_id: string; opening_float_minor: number }) => void
}) {
  const { t } = useTranslation()
  const [registerId, setRegisterId] = useState('')
  const [float, setFloat] = useState('')

  return (
    <form
      className="mt-6 space-y-4"
      onSubmit={(event) => {
        event.preventDefault()
        if (!registerId) return
        const value = Number.parseFloat(float.replace(',', '.'))
        onOpen({
          register_id: registerId,
          opening_float_minor: Number.isFinite(value) ? Math.round(value * 100) : 0,
        })
      }}
    >
      <label className="block text-caption text-content-muted">
        {t('pos.register')}
        <select
          value={registerId}
          onChange={(event) => setRegisterId(event.target.value)}
          required
          className="mt-1 h-12 w-full rounded-md border border-line bg-bg-base px-3 text-content-primary"
        >
          <option value="">—</option>
          {/* Une caisse tenue reste visible — on sait qui la tient — mais ne
              se choisit pas : le refus arrivait sinon après le comptage du fond. */}
          {registers.map((register) => (
            <option key={register.id} value={register.id} disabled={Boolean(register.held_by)}>
              {register.code}
              {register.location ? ` · ${register.location}` : ''}
              {register.held_by ? ` — ${t('pos.heldBy', { name: register.held_by })}` : ''}
            </option>
          ))}
        </select>
      </label>

      <label className="block text-caption text-content-muted">
        {t('pos.float')}
        <input
          inputMode="decimal"
          value={float}
          onChange={(event) => setFloat(event.target.value)}
          placeholder="0"
          className="mt-1 h-12 w-full rounded-md border border-line bg-bg-base px-3 text-content-primary"
        />
      </label>

      {error ? <p className="text-sm text-state-danger">{error}</p> : null}
      {loaded && registers.length === 0 ? (
        <p className="text-sm text-content-muted">{t('pos.noRegister')}</p>
      ) : null}

      <Button type="submit" size="lg" className="w-full" disabled={busy || !registerId}>
        {t('pos.open')}
      </Button>
    </form>
  )
}

/**
 * La clôture.
 *
 * On demande le **compté** avant de montrer le théorique : annoncer d'abord ce
 * qu'on devrait trouver invite à saisir ce chiffre-là plutôt qu'à compter le
 * tiroir, et l'écart disparaîtrait sans que personne ne l'ait vu.
 */
function CloseTill({
  expected,
  money,
  busy,
  error,
  onClose,
}: {
  expected: number
  money: (minor: number) => string
  busy: boolean
  error: string | null
  onClose: (payload: { counted_minor: number; note: string | null }) => void
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [counted, setCounted] = useState('')
  const [note, setNote] = useState('')

  const value = Number.parseFloat(counted.replace(',', '.'))
  const countedMinor = Number.isFinite(value) ? Math.round(value * 100) : null
  const variance = countedMinor === null ? null : countedMinor - expected

  if (!open) {
    return (
      <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
        {t('pos.close')}
      </Button>
    )
  }

  return (
    <Card className="w-full">
      <p className="overline text-content-muted">{t('pos.closeTitle')}</p>
      <label className="mt-3 block text-caption text-content-muted">
        {t('pos.counted')}
        <input
          inputMode="decimal"
          value={counted}
          onChange={(event) => setCounted(event.target.value)}
          autoFocus
          className="mt-1 h-12 w-full rounded-md border border-line bg-bg-base px-3 text-content-primary"
        />
      </label>

      {variance !== null && variance !== 0 ? (
        <>
          <p className="mt-3 text-sm text-ember-300">
            {t('pos.variance', { amount: money(variance) })}
          </p>
          <label className="mt-2 block text-caption text-content-muted">
            {t('pos.varianceNote')}
            <textarea
              value={note}
              onChange={(event) => setNote(event.target.value)}
              rows={2}
              className="mt-1 w-full rounded-md border border-line bg-bg-base px-3 py-2 text-sm text-content-primary"
            />
          </label>
        </>
      ) : null}

      {error ? <p className="mt-2 text-sm text-state-danger">{error}</p> : null}

      <div className="mt-4 flex gap-2">
        <Button
          disabled={busy || countedMinor === null || (variance !== 0 && note.trim().length < 3)}
          onClick={() =>
            onClose({ counted_minor: countedMinor ?? 0, note: note.trim() || null })
          }
        >
          {t('pos.confirmClose')}
        </Button>
        <Button variant="ghost" onClick={() => setOpen(false)}>
          {t('common.cancel')}
        </Button>
      </div>
    </Card>
  )
}

/**
 * Mes ventes de ce service — et le bouton pour en défaire une.
 *
 * Le motif est exigé, comme sur le ticket : « annulée » tout court ne
 * s'explique pas à la clôture. Une vente déjà annulée reste dans la liste,
 * barrée, avec son motif : c'est ce qu'on relit quand le tiroir surprend.
 */
function SalesPanel({
  sales,
  loading,
  money,
  busy,
  error,
  onVoid,
}: {
  sales: CounterSale[]
  loading: boolean
  money: (minor: number) => string
  busy: boolean
  error: string | null
  onVoid: (orderId: string, reason: string, done: () => void) => void
}) {
  const { t, i18n } = useTranslation()
  const [voiding, setVoiding] = useState<string | null>(null)
  const [reason, setReason] = useState('')

  return (
    <Card className="mt-6">
      <p className="overline text-content-muted">{t('pos.sales.title')}</p>
      <p className="mt-1 text-sm text-content-secondary">{t('pos.sales.hint')}</p>
      {error ? <p className="mt-2 text-sm text-state-danger">{error}</p> : null}
      {loading ? <Skeleton className="mt-4 h-24 w-full" /> : null}
      {!loading && sales.length === 0 ? (
        <p className="mt-4 text-sm text-content-muted">{t('pos.sales.empty')}</p>
      ) : null}
      <ul className="mt-4 divide-y divide-line-subtle border-t border-line-subtle">
        {sales.map((sale) => {
          const cancelled = sale.status === 'cancelled'
          return (
            <li key={sale.order_id} className="py-3">
              <div className="flex items-baseline justify-between gap-3">
                <span className="min-w-0">
                  <span
                    className={cn(
                      'block truncate text-sm text-content-primary',
                      cancelled && 'text-content-muted line-through',
                    )}
                  >
                    {sale.lines.map((line) => `${line.quantity} × ${line.label}`).join(', ')}
                  </span>
                  <span className="block text-caption text-content-muted">
                    <span className="font-mono">{sale.order_number}</span>
                    {sale.customer_label ? ` · ${sale.customer_label}` : ''}
                    {' · '}
                    {new Date(sale.created_at).toLocaleTimeString(i18n.language, {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                    {sale.payment_method
                      ? ` · ${t(`pos.methods.${sale.payment_method}`, { defaultValue: sale.payment_method })}`
                      : ''}
                  </span>
                </span>
                <span className="shrink-0 tabular-nums text-content-primary">
                  {money(sale.total_minor)}
                </span>
              </div>
              {cancelled ? (
                <p className="mt-1 text-caption text-ember-300">
                  {t('pos.sales.cancelled', { reason: sale.cancel_reason ?? '—' })}
                </p>
              ) : voiding === sale.order_id ? (
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <input
                    value={reason}
                    autoFocus
                    onChange={(event) => setReason(event.target.value)}
                    placeholder={t('pos.voidReason')}
                    className="h-10 min-w-0 flex-1 rounded-md border border-line bg-bg-base px-3 text-sm text-content-primary"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={busy || reason.trim().length < 2}
                    onClick={() =>
                      onVoid(sale.order_id, reason.trim(), () => {
                        setVoiding(null)
                        setReason('')
                      })
                    }
                  >
                    {t('pos.voidConfirm')}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setVoiding(null)}>
                    {t('pos.voidCancel')}
                  </Button>
                </div>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  className="mt-1"
                  onClick={() => {
                    setVoiding(sale.order_id)
                    setReason('')
                  }}
                >
                  {t('pos.sales.void')}
                </Button>
              )}
            </li>
          )
        })}
      </ul>
    </Card>
  )
}
